import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, MoreVertical, SearchX } from 'lucide-react';
import { Button, IconButton } from './Button';
import { SearchField } from './Field';
import { EmptyState, Skeleton } from './Feedback';
import { cx } from './cx';
import { cardColumns, filterRows, getLayout, getValue, nextSort, paginate, sortRows, splitColumns } from './dataTableUtils';
import styles from './DataTable.module.css';

function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const renderCell = (row, column, inTable = false) => {
  let content;
  if (column.render) content = column.render(row);
  else {
    const value = getValue(row, column);
    if (value == null || value === '') return <span className={styles.muted}>–</span>;
    content = String(value);
  }
  // clamp: Anzahl Zeilen in der Tabelle (in Karten/Details wird der volle Text gezeigt)
  if (inTable && column.clamp) {
    return <span className={styles.clamp} style={{ WebkitLineClamp: column.clamp }} title={typeof content === 'string' ? content : undefined}>{content}</span>;
  }
  return content;
};

function RowActions({ row, rowActions }) {
  const actions = (rowActions?.(row) || []).filter(Boolean);
  if (actions.length === 0) return null;
  return (
    <Menu>
      <MenuButton as={IconButton} icon={MoreVertical} label="Aktionen" size="sm" onClick={(e) => e.stopPropagation()} />
      <MenuItems anchor="bottom end" className={styles.menu} onClick={(e) => e.stopPropagation()}>
        {actions.map(({ label, icon: Icon, onClick, danger, disabled }) => (
          <MenuItem key={label} disabled={disabled}>
            <button type="button" disabled={disabled} className={cx(styles.menuItem, danger && styles.menuDanger)} onClick={() => onClick(row)}>
              {Icon && <Icon aria-hidden />}
              {label}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

function DetailList({ row, columns }) {
  if (columns.length === 0) return null;
  return (
    <dl className={styles.details}>
      {columns.map((column) => (
        <Fragment key={column.key}>
          <dt>{column.header}</dt>
          <dd className={column.align === 'end' ? styles.alignEnd : undefined}>{renderCell(row, column)}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

// Ganze Zeile anklickbar und per Tastatur (Tab + Enter) erreichbar. Bewusst ohne role="button",
// weil die Zeile selbst Schaltflächen enthält (Aufklappen, Aktionen).
const clickableProps = (onRowClick, row) => (onRowClick ? {
  onClick: () => onRowClick(row),
  onKeyDown: (e) => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onRowClick(row); }
  },
  tabIndex: 0
} : {});

/**
 * Responsive Tabelle für alle Listen der App.
 * Handy (< 640 px Containerbreite): Karten · Tablet: wichtige Spalten + Aufklappen · PC: volle Tabelle.
 *
 * Auswahl (optional): selection={{ selected: [keys], onChange: (keys) => … }} – Kästchen pro Zeile + „alle“.
 *
 * Spalten: { key, header, render?(row), accessor?(row), priority?: 1|2|3|4 (4 = nur in Details), primary?, secondary?,
 *            hideInCard?, sortable?, sortValue?(row), searchable?, searchValue?(row), align?: 'end', width?,
 *            clamp?: Zeilen (lange Texte in der Tabelle kürzen) }
 */
export function DataTable({
  columns,
  rows,
  rowKey = 'id',
  label,
  loading = false,
  onRowClick,
  rowActions,
  searchable = true,
  searchPlaceholder = 'Suchen …',
  toolbar,
  initialSort = null,
  pageSize = 25,
  emptyState,
  selection,
  className
}) {
  const [containerRef, width] = useElementWidth();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());

  const layout = getLayout(width);
  const keyOf = (row) => (typeof rowKey === 'function' ? rowKey(row) : row[rowKey]);

  const filtered = useMemo(() => filterRows(rows || [], query, columns), [rows, query, columns]);
  const sorted = useMemo(() => sortRows(filtered, sort, columns), [filtered, sort, columns]);
  const { items, page: currentPage, pageCount } = paginate(sorted, page, pageSize);
  const sortableColumns = columns.filter((c) => c.sortable);

  const changeQuery = (value) => { setQuery(value); setPage(1); };
  const changeSort = (value) => { setSort(value); setPage(1); };
  const toggleExpanded = (key) => setExpanded((set) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const selectedSet = new Set(selection?.selected || []);
  const filteredKeys = filtered.map(keyOf);
  const allSelected = filteredKeys.length > 0 && filteredKeys.every((k) => selectedSet.has(k));
  const toggleRow = (key) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    selection.onChange([...next]);
  };
  const toggleAll = () => {
    const next = new Set(selectedSet);
    filteredKeys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
    selection.onChange([...next]);
  };
  const checkbox = (key, label) => (
    <input
      type="checkbox"
      className={styles.checkbox}
      checked={selectedSet.has(key)}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      onChange={() => toggleRow(key)}
    />
  );

  const total = rows?.length || 0;
  const countText = query ? `${filtered.length} von ${total} Einträgen` : `${total} ${total === 1 ? 'Eintrag' : 'Einträge'}`;

  let content;
  if (loading) {
    content = layout === 'cards' ? (
      <div className={styles.cards}>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={styles.card}><Skeleton width="55%" height={18} /><Skeleton width="35%" /><Skeleton /></div>
        ))}
      </div>
    ) : (
      <div className={styles.skeletonRows}>{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={20} />)}</div>
    );
  } else if (total === 0) {
    content = <EmptyState title="Noch keine Einträge" {...emptyState} />;
  } else if (filtered.length === 0) {
    content = (
      <EmptyState
        icon={SearchX}
        title="Nichts gefunden"
        description={`Keine Treffer für „${query}“.`}
        action={<Button variant="secondary" onClick={() => changeQuery('')}>Suche zurücksetzen</Button>}
      />
    );
  } else if (layout === 'cards') {
    const { title, subtitle, details } = cardColumns(columns);
    content = (
      <ul className={styles.cards} aria-label={label}>
        {items.map((row) => (
          <li key={keyOf(row)} className={cx(styles.card, onRowClick && styles.clickable)} {...clickableProps(onRowClick, row)}>
            <div className={styles.cardHead}>
              {selection && checkbox(keyOf(row), 'Auswählen')}
              <div className={styles.cardTitles}>
                <span className={styles.cardTitle}>{renderCell(row, title)}</span>
                {subtitle && <span className={styles.cardSubtitle}>{renderCell(row, subtitle)}</span>}
              </div>
              <RowActions row={row} rowActions={rowActions} />
            </div>
            <DetailList row={row} columns={details} />
          </li>
        ))}
      </ul>
    );
  } else {
    const { visible, hidden } = splitColumns(columns, layout);
    const hasActions = typeof rowActions === 'function';
    const colSpan = visible.length + (hidden.length ? 1 : 0) + (hasActions ? 1 : 0) + (selection ? 1 : 0);
    content = (
      <div className={styles.tableWrap}>
        <table className={styles.table} aria-label={label}>
          <thead>
            <tr>
              {selection && (
                <th className={styles.narrow}>
                  <input type="checkbox" className={styles.checkbox} checked={allSelected} onChange={toggleAll} aria-label="Alle auswählen" />
                </th>
              )}
              {hidden.length > 0 && <th className={styles.narrow}><span className={styles.srOnly}>Details</span></th>}
              {visible.map((column) => {
                const active = sort?.key === column.key;
                const SortIcon = active ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th
                    key={column.key}
                    style={column.width ? { width: column.width } : undefined}
                    className={column.align === 'end' ? styles.alignEnd : undefined}
                    aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {column.sortable ? (
                      <button type="button" className={cx(styles.sortButton, active && styles.sortActive)} onClick={() => changeSort(nextSort(sort, column.key))}>
                        {column.header}
                        <SortIcon aria-hidden />
                      </button>
                    ) : column.header}
                  </th>
                );
              })}
              {hasActions && <th className={styles.narrow}><span className={styles.srOnly}>Aktionen</span></th>}
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const key = keyOf(row);
              const isOpen = expanded.has(key);
              return (
                <Fragment key={key}>
                  <tr className={cx(onRowClick && styles.clickable, (isOpen || selectedSet.has(key)) && styles.open)} {...clickableProps(onRowClick, row)}>
                    {selection && <td className={styles.narrow}>{checkbox(key, 'Auswählen')}</td>}
                    {hidden.length > 0 && (
                      <td className={styles.narrow}>
                        <IconButton
                          icon={ChevronDown}
                          size="sm"
                          label={isOpen ? 'Details ausblenden' : 'Details anzeigen'}
                          aria-expanded={isOpen}
                          className={cx(styles.chevron, isOpen && styles.chevronOpen)}
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(key); }}
                        />
                      </td>
                    )}
                    {visible.map((column) => (
                      <td key={column.key} className={column.align === 'end' ? styles.alignEnd : undefined}>{renderCell(row, column, true)}</td>
                    ))}
                    {hasActions && <td className={styles.narrow}><RowActions row={row} rowActions={rowActions} /></td>}
                  </tr>
                  {isOpen && (
                    <tr className={styles.detailRow}>
                      <td colSpan={colSpan}><DetailList row={row} columns={hidden} /></td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  const sortValue = sort ? `${sort.key}:${sort.direction}` : '';

  return (
    <div ref={containerRef} className={cx(styles.root, className)}>
      {(searchable || toolbar || selection || (layout === 'cards' && sortableColumns.length > 0)) && (
        <div className={styles.toolbar}>
          {searchable && <SearchField className={styles.search} value={query} onChange={changeQuery} placeholder={searchPlaceholder} />}
          {layout === 'cards' && sortableColumns.length > 0 && (
            <select
              className={styles.sortSelect}
              aria-label="Sortierung"
              value={sortValue}
              onChange={(e) => {
                const [key, direction] = e.target.value.split(':');
                changeSort(key ? { key, direction } : null);
              }}
            >
              <option value="">Sortierung</option>
              {sortableColumns.map((c) => (
                <Fragment key={c.key}>
                  <option value={`${c.key}:asc`}>{c.header} ↑</option>
                  <option value={`${c.key}:desc`}>{c.header} ↓</option>
                </Fragment>
              ))}
            </select>
          )}
          {selection && layout === 'cards' && filteredKeys.length > 0 && (
            <label className={styles.selectAll}>
              <input type="checkbox" className={styles.checkbox} checked={allSelected} onChange={toggleAll} />
              Alle auswählen
            </label>
          )}
          {toolbar && <div className={styles.toolbarExtra}>{toolbar}</div>}
        </div>
      )}

      {!loading && total > 0 && <p className={styles.count} aria-live="polite">{countText}</p>}

      {content}

      {!loading && pageCount > 1 && (
        <nav className={styles.pagination} aria-label="Seiten">
          <IconButton icon={ChevronLeft} label="Vorherige Seite" variant="secondary" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} />
          <span>Seite {currentPage} von {pageCount}</span>
          <IconButton icon={ChevronRight} label="Nächste Seite" variant="secondary" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)} />
        </nav>
      )}
    </div>
  );
}
